/**
 * AWS Transcribe streaming session — browser-direct real-time transcription.
 * Accepts 16kHz 16-bit LE PCM chunks and yields transcript fragments.
 *
 * Ported from product-noteble (apps/app/src/services/transcribeStreamingService.ts).
 * Uses Cognito Identity Pool credentials from ./awsClients.
 */

import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
  type AudioStream,
  type LanguageCode,
  type Result,
} from '@aws-sdk/client-transcribe-streaming';
import { getCredentialsProvider, AWS_REGION } from './awsClients';

const SAMPLE_RATE = 16000;

export interface TranscriptFragment {
  index: number;
  text: string;
  startTime: number;
  endTime: number;
  isFinal: boolean;
}

export interface TranscribeStreamingCallbacks {
  onTranscript: (fragment: TranscriptFragment) => void;
  onError: (error: Error) => void;
  onClose: () => void;
}

/** Map a Transcribe result to a fragment (pure — unit-tested). */
export function fragmentFromResult(result: Result, index: number): TranscriptFragment | null {
  const alt = result.Alternatives?.[0];
  if (!alt) return null;
  return {
    index,
    text: alt.Transcript || '',
    startTime: result.StartTime || 0,
    endTime: result.EndTime || 0,
    isFinal: !result.IsPartial,
  };
}

/** Build a human-readable error with the AWS request id, if any (pure). */
export function describeTranscribeError(error: unknown): Error {
  let message = 'Failed to start transcription session';
  let requestId = 'unknown';
  if (error instanceof Error) message = error.message || message;
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    const meta = e.$metadata as { requestId?: string } | undefined;
    requestId = meta?.requestId || (e.requestId as string) || requestId;
    const name = e.name as string | undefined;
    if (name && name !== 'Error') message = `${name}: ${message}`;
  }
  return new Error(`${message} (Request ID: ${requestId})`);
}

function createAudioStream(
  getNextChunk: () => Promise<Uint8Array | null>,
): AsyncIterable<AudioStream> {
  return {
    [Symbol.asyncIterator]() {
      return {
        async next(): Promise<IteratorResult<AudioStream>> {
          const chunk = await getNextChunk();
          if (chunk === null) return { done: true, value: undefined };
          return { done: false, value: { AudioEvent: { AudioChunk: chunk } } };
        },
      };
    },
  };
}

export class TranscribeStreamingSession {
  private client: TranscribeStreamingClient | null = null;
  private audioQueue: Uint8Array[] = [];
  private audioResolve: (() => void) | null = null;
  private stopped = false;
  private fragmentIndex = 0;
  private callbacks: TranscribeStreamingCallbacks;
  private languageCode: string;

  constructor(languageCode: string, callbacks: TranscribeStreamingCallbacks) {
    this.languageCode = languageCode;
    this.callbacks = callbacks;
  }

  /** Start the session. Resolves when the stream ends; errors go to onError. */
  async start(): Promise<void> {
    let credentials;
    try {
      credentials = await getCredentialsProvider();
    } catch (error) {
      this.callbacks.onError(describeTranscribeError(error));
      return;
    }
    // stop() may have run during the credential round-trip; if so, abort
    // before opening a client that nothing owns.
    if (this.stopped) return;

    this.client = new TranscribeStreamingClient({ region: AWS_REGION, credentials });

    const getNextChunk = (): Promise<Uint8Array | null> =>
      new Promise((resolve) => {
        if (this.stopped) return resolve(null);
        if (this.audioQueue.length > 0) return resolve(this.audioQueue.shift()!);
        this.audioResolve = () => {
          resolve(this.stopped || this.audioQueue.length === 0 ? null : this.audioQueue.shift()!);
        };
      });

    try {
      // Constructed inside the try so a synchronous throw still reaches onError.
      const command = new StartStreamTranscriptionCommand({
        LanguageCode: this.languageCode as LanguageCode,
        MediaEncoding: 'pcm',
        MediaSampleRateHertz: SAMPLE_RATE,
        AudioStream: createAudioStream(getNextChunk),
        EnablePartialResultsStabilization: true,
        PartialResultsStability: 'medium',
      });
      const response = await this.client.send(command);
      if (response.TranscriptResultStream) {
        // Drain to completion. After stop() signals end-of-audio, Transcribe
        // still delivers the final (IsPartial=false) segment before the stream
        // ends — breaking early here would drop the last utterance.
        for await (const event of response.TranscriptResultStream) {
          const results = event.TranscriptEvent?.Transcript?.Results;
          if (!results) continue;
          for (const result of results) {
            const fragment = fragmentFromResult(result, this.fragmentIndex);
            if (fragment) {
              this.fragmentIndex++;
              this.callbacks.onTranscript(fragment);
            }
          }
        }
      }
      this.callbacks.onClose();
    } catch (error) {
      if (!this.stopped) this.callbacks.onError(describeTranscribeError(error));
    } finally {
      this.client?.destroy();
      this.client = null;
    }
  }

  /** Push a PCM audio chunk to the stream. */
  pushAudio(chunk: Uint8Array): void {
    if (this.stopped) return;
    this.audioQueue.push(chunk);
    if (this.audioResolve) {
      const resolve = this.audioResolve;
      this.audioResolve = null;
      resolve();
    }
  }

  /**
   * Stop the session. Signals end-of-audio so Transcribe delivers the final
   * segment; the client is destroyed by start()'s finally once the result
   * stream drains. Idempotent.
   */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.audioResolve) {
      const resolve = this.audioResolve;
      this.audioResolve = null;
      resolve();
    }
  }
}
